import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
// import { addAccount as addAccountApi, AddAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import { updateCart } from "@insite/client-framework/Services/CartService";
import {
    deleteSession,
    stopWatchingForOtherTabSessionChange,
    watchForOtherTabSessionChange,
} from "@insite/client-framework/Services/SessionService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { AccountModel, BaseAddressModel } from "@insite/client-framework/Types/ApiModels";
import { AddAccountApiParameter, addAccountService } from "@tFissco/Services/AddAccountService";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";

export interface AddAccountParameter {
    IsCustomerNumberProvided: boolean;
    accountNumber: string;
    firstName: string;
    lastName: string;
    phone: string;
    userName: string;
    email: string;
    password: string;
    isSubscribed: boolean;
    companyName: string;
    address1: string;
    address2: string;
    city: string;
    postalCode: string;
    IsShipToSameAsBilling: boolean;
    address1ShipTo: string;
    address2ShipTo: string;
    cityShipTo: string;
    zipShipTo: string;
    IsCreditAccount: boolean;
    selectedCountry: string | undefined;
    selectedhearAboutUsOption: string | undefined;
    selectedSearchEngine: string | undefined;
    selectedSocialMedia: string | undefined;
    hearAboutUsInput: any;
    selectedbtState: any;
    selectedStCountry: string | undefined;
    selectedStState: any;
    BillToDefaultWarehouseId: any;
    BillToDefaultWarehouseName: any;
    ShipToDefaultWarehouseName: any;
    ShipToDefaultWarehouseId: any;
    title?: string;
    fax?: string;
    epaAttachment: any;
    rescaleAttachment: any;
    tdrlAttachment: any;
    onSuccess?: (account?: AccountModel) => void;
    onError?: (error: string, statusCode?: number) => void;
}

type HandlerType = ApiHandlerDiscreteParameter<
    AddAccountParameter,
    AddAccountApiParameter,
    ServiceResult<AccountModel>,
    { currentUserIsGuest?: boolean }
>;

export const UnassignCartFromGuest: HandlerType = async props => {
    const { value: cart } = getCurrentCartState(props.getState());
    props.currentUserIsGuest = getCurrentUserIsGuest(props.getState());
    if (!props.currentUserIsGuest) {
        return;
    }
    if (!cart) {
        throw new Error("The cart is not loaded. Try reloading the page.");
    }

    if (cart.lineCount > 0) {
        const cartClone = cloneDeep(cart) as Draft<typeof cart>;
        cartClone.unassignCart = true;
        await updateCart({ cart: cartClone });
    }
};

export const SignOutGuest: HandlerType = async props => {
    if (!props.currentUserIsGuest) {
        return;
    }

    await deleteSession();
    props.dispatch({
        type: "Context/DeleteSession",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    let howDidYouHearAboutUs; // Declare the variable here

    if (props.parameter.selectedhearAboutUsOption === "Search Engine") {
        // Format howDidYouHearAboutUs for Search Engine option
        const selectedSearchEngine = props.parameter.selectedSearchEngine;
        howDidYouHearAboutUs = selectedSearchEngine ? `Search Engine; ${selectedSearchEngine}` : undefined;
    } else if (props.parameter.selectedhearAboutUsOption === "Social Media") {
        const selectedSocialMedia = props.parameter.selectedSocialMedia;
        howDidYouHearAboutUs = selectedSocialMedia ? `Social Media; ${selectedSocialMedia}` : undefined;
    } else if (props.parameter.selectedhearAboutUsOption === "Other") {
        const hearAboutUsInputValue = props.parameter.hearAboutUsInput;
        howDidYouHearAboutUs = hearAboutUsInputValue ? `Other; ${hearAboutUsInputValue}` : undefined;
    } else {
        // For other options, use the selectedhearAboutUsOption directly
        howDidYouHearAboutUs = props.parameter.selectedhearAboutUsOption
            ? props.parameter.selectedhearAboutUsOption
            : undefined;
    }
    props.apiParameter = {
        account: {
            ...props.parameter,
            properties: {
                IsCustomerNumberProvided: props.parameter.IsCustomerNumberProvided,
                CustomerNumber: props.parameter.accountNumber,
                howDidYouHearAboutUs,
                UserPhone: props.parameter.phone,
                Company: props.parameter.companyName,
                Address1: props.parameter.address1,
                Address2: props.parameter.address2,
                City: props.parameter.city,
                Zip: props.parameter.postalCode,
                IsShipToSameAsBilling: props.parameter.IsShipToSameAsBilling,
                StateBillTo: props.parameter.selectedbtState,
                CountryBillTo: props.parameter.selectedCountry,
                BillToDefaultWarehouseId: props.parameter.BillToDefaultWarehouseId,
                BillToDefaultWarehouseName: props.parameter.BillToDefaultWarehouseName,
                ShipToDefaultWarehouseName: props.parameter.ShipToDefaultWarehouseName,
                ShipToDefaultWarehouseId: props.parameter.ShipToDefaultWarehouseId,

                FirstNameShipTo: props.parameter.firstName,
                LastNameShipTo: props.parameter.lastName,
                Address1ShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.address1
                    : props.parameter.address1ShipTo,
                Address2ShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.address2
                    : props.parameter.address2ShipTo,
                CityShipTo: props.parameter.IsShipToSameAsBilling ? props.parameter.city : props.parameter.cityShipTo,
                ZipShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.postalCode
                    : props.parameter.zipShipTo,
                StateShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.selectedbtState
                    : props.parameter.selectedStState,
                CountryShipTo: props.parameter.IsShipToSameAsBilling
                    ? props.parameter.selectedCountry
                    : props.parameter.selectedStCountry,
                Title: props.parameter.title,
                Fax: props.parameter.fax,
                EpaAttachment: props.parameter.epaAttachment,
                RescaleAttachment: props.parameter.rescaleAttachment,
                TdrlAttachment: props.parameter.tdrlAttachment,
            },
        } as unknown as AccountModel,
    };
};

export const AddAccount: HandlerType = async props => {
    stopWatchingForOtherTabSessionChange();
    props.apiResult = await addAccountService(props.apiParameter);
    watchForOtherTabSessionChange();
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!, props.apiResult.statusCode);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.();
    }
};

export const chain = [
    UnassignCartFromGuest,
    SignOutGuest,
    PopulateApiParameter,
    AddAccount,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];

const addAccount = createHandlerChainRunner(chain, "AddAccount");
export default addAccount;
